import { bootstrapApplication } from '@angular/platform-browser';
import { RouteReuseStrategy, provideRouter, withPreloading, PreloadAllModules } from '@angular/router';
import { IonicRouteStrategy, provideIonicAngular } from '@ionic/angular/standalone';
import { initializeApp, provideFirebaseApp, getApp } from '@angular/fire/app';
import {
  getAuth,
  initializeAuth,
  browserLocalPersistence,
  provideAuth,
  connectAuthEmulator
} from '@angular/fire/auth';
import { Capacitor } from '@capacitor/core';
import { getFirestore, initializeFirestore, provideFirestore, connectFirestoreEmulator } from '@angular/fire/firestore';
import { getStorage, provideStorage, connectStorageEmulator } from '@angular/fire/storage';
import { getFunctions, provideFunctions, connectFunctionsEmulator } from '@angular/fire/functions';
import { provideHttpClient } from '@angular/common/http';
import { provideTranslateService, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader, provideTranslateHttpLoader } from '@ngx-translate/http-loader';

import { routes } from './app/app.routes';
import { AppComponent } from './app/app.component';
import { environment } from './environments/environment';

bootstrapApplication(AppComponent, {
  providers: [
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    provideIonicAngular(),
    provideRouter(routes, withPreloading(PreloadAllModules)),
    provideFirebaseApp(() => initializeApp(environment.firebaseConfig)),
    provideAuth(() => {
      // En WebViews nativos (WKWebView de iOS y WebView de Android) la
      // persistencia por defecto en IndexedDB puede colgarse al abrir la base,
      // dejando a authStateReady() sin resolver. localStorage es síncrono y
      // confiable en ese entorno. En web se mantiene el getAuth() por defecto.
      //
      // NO se pasa popupRedirectResolver en nativo: ese resolver carga gapi + un
      // iframe del authDomain (firebaseapp.com) cuyo handshake postMessage nunca
      // completa contra el origen capacitor://localhost del WKWebView de iOS,
      // colgando authStateReady() y la finalización del login. La app solo usa
      // email/password (nunca popup/redirect), así que el resolver es innecesario.
      const auth = Capacitor.isNativePlatform()
        ? initializeAuth(getApp(), { persistence: browserLocalPersistence })
        : getAuth();
      if (environment.useEmulators) {
        connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
      }
      return auth;
    }),
    provideFirestore(() => {
      // En el WKWebView de iOS el transporte WebChannel por defecto de Firestore
      // puede no establecer conexión; autoDetectLongPolling cae a long-polling
      // solo si detecta el problema (mantiene WebChannel si funciona). En web se
      // usa getFirestore() por defecto.
      const firestore = Capacitor.isNativePlatform()
        ? initializeFirestore(getApp(), { experimentalAutoDetectLongPolling: true })
        : getFirestore();
      if (environment.useEmulators) {
        connectFirestoreEmulator(firestore, 'localhost', 8080);
      }
      return firestore;
    }),
    provideStorage(() => {
      const storage = getStorage();
      if (environment.useEmulators) {
        connectStorageEmulator(storage, 'localhost', 9199);
      }
      return storage;
    }),
    provideFunctions(() => {
      const functions = getFunctions();
      if (environment.useEmulators) {
        connectFunctionsEmulator(functions, 'localhost', 5001);
      }
      return functions;
    }),
    provideHttpClient(),
    provideTranslateHttpLoader(),
    provideTranslateService({
      defaultLanguage: 'es',
      loader: {
        provide: TranslateLoader,
        useClass: TranslateHttpLoader
      }
    })
  ],
});
